import { FormEvent, useEffect, useMemo, useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { SampleListParams } from '../../services/sample.service';
import { BSL_LEVELS, SAMPLE_STATUS } from '../../constants/sample.constants';
import { SAMPLE_TYPE_CODE_ORDER } from '../../utils/sampleTypeLabels';
import type { StorageRoom } from '../../services/storageRoom.service';
import NotesFilterFields from './NotesFilterFields';
import SampleDateRangeFilterRow from './SampleDateRangeFilterRow';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import {
  buildStorageRoomSelectLabels,
  formatStorageRoomSelectOption,
} from '../../utils/storageRoomSearch';
import TextContentSep from '../common/TextContentSep';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** hover 用。値の有無に関わらず「フィールド · 表示値」 */
function filterSelectTitle(fieldLabel: string, valueShown: string): string {
  return joinLabeledValue(fieldLabel, valueShown);
}

function statusFilterLabel(status: string | undefined, t: TranslateFn, allLabel: string): string {
  if (status === SAMPLE_STATUS.AVAILABLE) return t('sample_filter.status_available');
  if (status === SAMPLE_STATUS.OUT_OF_STOCK) return t('sample_filter.status_out_of_stock');
  if (status === SAMPLE_STATUS.DISPOSED) return t('sample_filter.status_disposed');
  return allLabel;
}

export type StorageRoomOption = Pick<StorageRoom, 'id' | 'name' | 'location' | 'user'>;

export type SampleDetailedFilterFormProps = {
  localFilters: SampleListParams;
  isAdmin: boolean;
  hideLocationFilters: boolean;
  storageRooms: StorageRoomOption[];
  moreFiltersOpen: boolean;
  preface?: React.ReactNode;
  onSubmit: (e: FormEvent) => void;
  onSelectChange: (field: keyof SampleListParams, value: string | number | undefined) => void;
  onDebouncedChange: (field: keyof SampleListParams, value: string | number | boolean | undefined) => void;
  onNotesChange: (notes: string | undefined) => void;
  onNotesIsEmptyChange: (isEmpty: boolean) => void;
};

/** 場所・備考・日付ブロックに何か入っているか（折りたたみ自動展開用） */
export function hasSampleMoreFilters(filters: SampleListParams, isAdmin: boolean): boolean {
  return Boolean(
    filters.notes ||
      filters.notesIsEmpty ||
      filters.drawerNumber ||
      filters.caseName ||
      filters.createdDate ||
      filters.lastUsedDate ||
      filters.createdDateVal != null ||
      filters.lastUsedDateVal != null ||
      (isAdmin && filters.preparer)
  );
}

/** 詳細フィルター本体（状態は親が持つ） */
export default function SampleDetailedFilterForm({
  localFilters,
  isAdmin,
  hideLocationFilters,
  storageRooms,
  moreFiltersOpen,
  preface,
  onSubmit,
  onSelectChange,
  onDebouncedChange,
  onNotesChange,
  onNotesIsEmptyChange,
}: SampleDetailedFilterFormProps) {
  const { t, i18n } = useTranslation();
  const moreDetailsRef = useRef<HTMLDetailsElement>(null);
  const openedForActiveRef = useRef(false);

  // 値があるとき一度だけ開く。controlled `open` は打鍵中の開閉争奪を起こすので使わない
  useEffect(() => {
    const el = moreDetailsRef.current;
    if (!el) return;
    if (moreFiltersOpen) {
      if (!openedForActiveRef.current) {
        el.open = true;
        openedForActiveRef.current = true;
      }
    } else {
      openedForActiveRef.current = false;
    }
  }, [moreFiltersOpen]);

  const allLabel = t('common.all');
  const storageSelectLabels = useMemo(
    () => buildStorageRoomSelectLabels(storageRooms, i18n.language),
    [storageRooms, i18n.language]
  );
  const storageRoomTitleShown = (() => {
    const room = storageRooms.find((r) => r.id === localFilters.storageRoomId);
    if (!room) return allLabel;
    return formatStorageRoomSelectOption(room, storageSelectLabels, t);
  })();

  return (
    <Form className="sample-detailed-filter" onSubmit={onSubmit}>
      {preface}
      <Row className="mb-1 g-1 sample-detailed-filter-primary">
        <Col xs={4} md={3} lg>
          <Form.Label htmlFor="filter-sample-type" className="sample-filter-field-label">
            {t('sample.sample_type')}
          </Form.Label>
          <Form.Select
            id="filter-sample-type"
            name="sampleType"
            value={localFilters.sampleType || ''}
            onChange={(e) => onSelectChange('sampleType', e.target.value)}
            aria-label={t('sample.sample_type')}
            title={filterSelectTitle(
              t('sample.sample_type'),
              localFilters.sampleType ? t(`sample.sample_types.${localFilters.sampleType}`) : allLabel
            )}
          >
            <option value="">{allLabel}</option>
            {SAMPLE_TYPE_CODE_ORDER.map((code) => (
              <option key={code} value={code}>
                {t(`sample.sample_types.${code}`)}
              </option>
            ))}
          </Form.Select>
          <TextContentSep />
        </Col>
        <Col xs={4} md={3} lg>
          <Form.Label htmlFor="filter-pathogen" className="sample-filter-field-label">
            {t('sample_filter.pathogen')}
          </Form.Label>
          <Form.Control
            id="filter-pathogen"
            name="pathogen"
            type="text"
            placeholder={t('sample_filter.pathogen_placeholder', {
              defaultValue: t('sample_filter.pathogen'),
            })}
            value={localFilters.pathogenGenus || ''}
            onChange={(e) => onDebouncedChange('pathogenGenus', e.target.value)}
            autoComplete="off"
            aria-label={t('sample_filter.pathogen')}
            title={t('sample_filter.pathogen_placeholder', {
              defaultValue: t('sample_filter.pathogen'),
            })}
          />
          <TextContentSep />
        </Col>
        <Col xs={4} md={3} lg>
          <Form.Label
            htmlFor="filter-bsl-level"
            className="sample-filter-field-label"
            title={t('sample.bsl_level')}
          >
            {t('sample.bsl_level_short', { defaultValue: 'BSL' })}
          </Form.Label>
          <Form.Select
            id="filter-bsl-level"
            name="bslLevel"
            value={localFilters.bslLevel || ''}
            onChange={(e) => onSelectChange('bslLevel', e.target.value)}
            aria-label={t('sample.bsl_level_short', { defaultValue: 'BSL' })}
            title={filterSelectTitle(
              t('sample.bsl_level'),
              localFilters.bslLevel ? String(localFilters.bslLevel) : allLabel
            )}
          >
            <option value="">{allLabel}</option>
            {BSL_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </Form.Select>
          <TextContentSep />
        </Col>
        <Col xs={6} md={3} lg>
          <Form.Label htmlFor="filter-status" className="sample-filter-field-label">
            {t('sample_filter.status')}
          </Form.Label>
          <Form.Select
            id="filter-status"
            name="status"
            value={localFilters.status || ''}
            onChange={(e) => onSelectChange('status', e.target.value)}
            autoComplete="off"
            aria-label={t('sample_filter.status')}
            title={filterSelectTitle(
              t('sample_filter.status'),
              statusFilterLabel(localFilters.status, t, allLabel)
            )}
          >
            <option value="">{allLabel}</option>
            <option value={SAMPLE_STATUS.AVAILABLE}>{t('sample_filter.status_available')}</option>
            <option value={SAMPLE_STATUS.OUT_OF_STOCK}>{t('sample_filter.status_out_of_stock')}</option>
            <option value={SAMPLE_STATUS.DISPOSED}>{t('sample_filter.status_disposed')}</option>
          </Form.Select>
          <TextContentSep />
        </Col>
        {!hideLocationFilters ? (
          <Col xs={6} md={4} lg>
            <Form.Label htmlFor="filter-storage-room" className="sample-filter-field-label">
              {t('sample.storage_room')}
            </Form.Label>
            <Form.Select
              id="filter-storage-room"
              name="storageRoomId"
              value={localFilters.storageRoomId ?? ''}
              onChange={(e) =>
                onSelectChange('storageRoomId', e.target.value ? Number(e.target.value) : undefined)
              }
              aria-label={t('sample.storage_room')}
              title={filterSelectTitle(t('sample.storage_room'), storageRoomTitleShown)}
            >
              <option value="">{allLabel}</option>
              {storageRooms.map((room) => {
                const label = formatStorageRoomSelectOption(room, storageSelectLabels, t);
                return (
                  <option key={room.id} value={room.id} title={label}>
                    {label}
                  </option>
                );
              })}
            </Form.Select>
          </Col>
        ) : null}
      </Row>

      {/* 追加条件。適用は debounce／Enter（視覚的な検索ボタンは冗長） */}
      <div className="sample-detailed-filter-foot">
        <details ref={moreDetailsRef} className="sample-detailed-filter-dates mb-0">
          <summary
            className="sample-detailed-filter-dates-summary"
            title={t('sample_filter.more_filters_title', {
              defaultValue: t('sample_filter.more_filters'),
            })}
          >
            {t('sample_filter.more_filters')}
          </summary>
          <Row className="mb-2 g-2 mt-1">
            {isAdmin && (
              <Col xs={6} md={3} lg={2}>
                <Form.Label
                  htmlFor="filter-preparer"
                  className="sample-filter-field-label"
                  title={t('sample_filter.preparer_title', {
                    defaultValue: t('sample.maker_name'),
                  })}
                >
                  {t('sample_filter.preparer')}
                </Form.Label>
                <Form.Control
                  id="filter-preparer"
                  name="preparer"
                  type="text"
                  placeholder={t('sample_filter.preparer_placeholder')}
                  value={localFilters.preparer || ''}
                  onChange={(e) => onDebouncedChange('preparer', e.target.value)}
                  autoComplete="off"
                  aria-label={t('sample_filter.preparer')}
                  title={t('sample_filter.preparer_title', {
                    defaultValue: t('sample.maker_name'),
                  })}
                />
              </Col>
            )}
            <Col xs={12} md={hideLocationFilters ? (isAdmin ? 9 : 12) : isAdmin ? 5 : 6}>
              <NotesFilterFields
                notesId="filter-notes"
                notesEmptyId="filter-notes-empty"
                notesValue={localFilters.notes || ''}
                notesIsEmpty={localFilters.notesIsEmpty === true}
                onNotesChange={onNotesChange}
                onNotesIsEmptyChange={onNotesIsEmptyChange}
                labelClassName="sample-filter-field-label"
              />
            </Col>
            {!hideLocationFilters ? (
              <>
                <Col xs={6} md={2}>
                  <Form.Label
                    htmlFor="filter-drawer-number"
                    className="sample-filter-field-label"
                    title={t('sample_filter.drawer_number')}
                  >
                    {t('sample_filter.drawer_number_short', {
                      defaultValue: t('sample.drawer_short', { defaultValue: '引き出し' }),
                    })}
                  </Form.Label>
                  <Form.Control
                    id="filter-drawer-number"
                    name="drawerNumber"
                    type="text"
                    placeholder={t('sample_filter.drawer_number_placeholder', {
                      defaultValue: t('sample_filter.drawer_number'),
                    })}
                    value={localFilters.drawerNumber || ''}
                    onChange={(e) => onDebouncedChange('drawerNumber', e.target.value)}
                    autoComplete="off"
                    aria-label={t('sample_filter.drawer_number_short', {
                      defaultValue: t('sample.drawer_short', { defaultValue: '引き出し' }),
                    })}
                    title={t('sample_filter.drawer_number')}
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label htmlFor="filter-case-name" className="sample-filter-field-label">
                    {t('sample_filter.case_name')}
                  </Form.Label>
                  <Form.Control
                    id="filter-case-name"
                    name="caseName"
                    type="text"
                    placeholder={t('sample_filter.case_name_placeholder', {
                      defaultValue: t('sample_filter.case_name'),
                    })}
                    value={localFilters.caseName || ''}
                    onChange={(e) => onDebouncedChange('caseName', e.target.value)}
                    autoComplete="off"
                    aria-label={t('sample_filter.case_name')}
                    title={t('sample_filter.case_name_placeholder', {
                      defaultValue: t('sample_filter.case_name'),
                    })}
                  />
                </Col>
              </>
            ) : null}
          </Row>
          <div className="sample-date-range-pair">
            <SampleDateRangeFilterRow
              dateValue={localFilters.createdDate || ''}
              dateOnChange={(v) => onDebouncedChange('createdDate', v)}
              dirValue={localFilters.createdDateDir ?? 'both'}
              dirOnChange={(v) => onDebouncedChange('createdDateDir', v)}
              valValue={localFilters.createdDateVal}
              valOnChange={(v) => onDebouncedChange('createdDateVal', v)}
              unitValue={localFilters.createdDateUnit ?? 'day'}
              unitOnChange={(v) => onDebouncedChange('createdDateUnit', v)}
              dateLabelKey="sample_filter.created_date"
              t={t}
              language={i18n.language}
            />
            <SampleDateRangeFilterRow
              dateValue={localFilters.lastUsedDate || ''}
              dateOnChange={(v) => onDebouncedChange('lastUsedDate', v)}
              dirValue={localFilters.lastUsedDateDir ?? 'both'}
              dirOnChange={(v) => onDebouncedChange('lastUsedDateDir', v)}
              valValue={localFilters.lastUsedDateVal}
              valOnChange={(v) => onDebouncedChange('lastUsedDateVal', v)}
              unitValue={localFilters.lastUsedDateUnit ?? 'day'}
              unitOnChange={(v) => onDebouncedChange('lastUsedDateUnit', v)}
              dateLabelKey="sample_filter.last_used_date"
              t={t}
              language={i18n.language}
            />
          </div>
        </details>
      </div>
      {/* 複数フィールド時の Enter 送信用（視覚 UI は debounce に任せる） */}
      <button
        type="submit"
        className="visually-hidden"
        aria-label={t('sample_filter.detailed_filter', {
          defaultValue: t('common.search'),
        })}
        title={t('sample_filter.apply_detailed_title', {
          defaultValue: t('sample_filter.detailed_filter'),
        })}
      >
        {t('common.search')}
      </button>
    </Form>
  );
}
